/**
 * Maidly Business Manager - License Management System
 * 
 * This module handles:
 * - License validation with server
 * - Encrypted local storage of license data (secureIDB)
 * - Automatic synchronization
 * - Feature gating based on license tier
 * - Expiration warnings
 *
 * Storage
 * -------
 * As of Phase-5 hardening, none of the fields below are kept in plain
 * localStorage. They live in `secureIDB` (AES-GCM encrypted IndexedDB
 * with a non-extractable per-origin key), so a smash-and-grab XSS or a
 * stolen browser profile can't read the license or device fingerprints.
 * On first launch we transparently migrate any pre-existing plaintext
 * values so returning users don't have to re-activate.
 */

import {
    secureGet,
    secureSet,
    secureRemove,
    migrateFromLocalStorage,
} from './secureIDB';

const STORAGE_KEY = 'maidly_license_data';
const LAST_CHECK_KEY = 'maidly_last_check';
const DEVICE_ID_KEY = 'maidly_device_id';
const TRIAL_KEY = 'maidly_first_launch';

// Fire-and-forget migration of any legacy localStorage entries. Safe to
// call at module load — a no-op once nothing plaintext is left.
migrateFromLocalStorage([STORAGE_KEY, LAST_CHECK_KEY, DEVICE_ID_KEY, TRIAL_KEY])
    .catch((err) => console.warn('[LicenseManager] legacy migration skipped', err));

class LicenseManager {
    constructor() {
        this.API_URL = process.env.REACT_APP_BACKEND_URL || '';
        this.STORAGE_KEY = STORAGE_KEY;
        this.LAST_CHECK_KEY = LAST_CHECK_KEY;
        this.DEVICE_ID_KEY = DEVICE_ID_KEY;
        this.TRIAL_KEY = TRIAL_KEY;
        this.CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
        this.TRIAL_DAYS = 15;
        
        this.licenseData = null;
        this.isValid = false;
        this.features = [];
        this.tier = 'free';
        // Populated by initialize(); synchronous read for getLicenseInfo.
        this.trialStatus = { isTrialMode: true, trialDaysRemaining: this.TRIAL_DAYS, isExpired: false };

        // Ephemeral in-memory mirror of the device ID so the synchronous
        // parts of the app (SyncService, telemetry) don't have to await
        // an IDB read on every call. Populated on first `getDeviceId()`.
        this._deviceIdMemo = null;
    }

    /**
     * Generate or get device ID (async — persists in encrypted IDB).
     */
    async getDeviceId() {
        if (this._deviceIdMemo) return this._deviceIdMemo;
        let deviceId = await secureGet(this.DEVICE_ID_KEY);
        if (!deviceId) {
            deviceId = 'device-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
            await secureSet(this.DEVICE_ID_KEY, deviceId);
        }
        this._deviceIdMemo = deviceId;
        return deviceId;
    }

    /**
     * Check trial status (async — persists first-launch timestamp in IDB).
     */
    async checkTrialStatus() {
        const firstLaunch = await secureGet(this.TRIAL_KEY);
        const now = new Date().getTime();

        if (!firstLaunch) {
            // First time user
            await secureSet(this.TRIAL_KEY, now.toString());
            return {
                isTrialMode: true,
                trialDaysRemaining: this.TRIAL_DAYS,
                isExpired: false
            };
        }

        const daysSinceFirstLaunch = Math.floor((now - parseInt(firstLaunch)) / (1000 * 60 * 60 * 24));
        const trialDaysRemaining = Math.max(0, this.TRIAL_DAYS - daysSinceFirstLaunch);

        return {
            isTrialMode: true,
            trialDaysRemaining,
            isExpired: trialDaysRemaining === 0
        };
    }

    /**
     * Initialize the license manager
     */
    async initialize() {
        console.log('🔐 Initializing License Manager...');
        
        // Cache trial status so getLicenseInfo/getStatus can stay sync.
        this.trialStatus = await this.checkTrialStatus();

        // Try to load cached license
        const cached = await this.loadFromCache();
        
        if (cached && cached.license_key) {
            // Validate cached license with server
            const needsCheck = await this.shouldCheckServer();
            
            if (needsCheck) {
                console.log('🔄 Validating license with server...');
                await this.validateWithServer(cached.license_key);
            } else {
                console.log('✅ Using cached license (valid until next check)');
                this.licenseData = cached;
                this.isValid = true;
                this.features = cached.features || [];
                this.tier = cached.tier || 'pro';
            }
        } else {
            // No license, check trial
            const trialStatus = await this.checkTrialStatus();
            if (!trialStatus.isExpired) {
                console.log(`📅 Trial mode: ${trialStatus.trialDaysRemaining} days remaining`);
                this.isValid = false;
                this.tier = 'trial';
            } else {
                console.log('⚠️ Trial expired - activation required');
                this.isValid = false;
                this.tier = 'expired';
            }
        }
        
        return this.isValid;
    }

    /**
     * Validate license with server
     */
    async validateWithServer(licenseKey) {
        try {
            const deviceId = await this.getDeviceId();
            
            const response = await fetch(`${this.API_URL}/api/license/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    license_key: licenseKey,
                    device_id: deviceId
                })
            });

            const result = await response.json();

            if (result.valid) {
                // License is valid
                this.isValid = true;
                this.licenseData = {
                    license_key: licenseKey,
                    device_id: deviceId,
                    days_remaining: result.days_remaining,
                    expiry_date: result.expiry_date,
                    status: 'active',
                    tier: 'pro',
                    features: ['All Features', 'Unlimited Invoices', 'Digital Signatures', 'Customer Management', 'Reports']
                };
                this.features = this.licenseData.features;
                this.tier = 'pro';
                
                // Save to cache
                await this.saveToCache(this.licenseData);
                await this.updateLastCheck();
                
                console.log('✅ License validated successfully');
                console.log(`   Days remaining: ${result.days_remaining}`);
                
                return true;
            } else {
                // License is invalid
                this.isValid = false;
                await this.clearCache();
                
                console.log('❌ License validation failed:', result.message);
                
                return false;
            }
        } catch (error) {
            console.error('🔴 License validation error:', error);
            
            // If offline, try to use cached data
            const cached = await this.loadFromCache();
            if (cached) {
                console.log('📴 Offline - using cached license');
                this.licenseData = cached;
                this.isValid = true;
                this.features = cached.features || [];
                this.tier = cached.tier || 'pro';
                return true;
            }
            
            return false;
        }
    }

    /**
     * Activate license with key
     */
    async activate(licenseKey) {
        if (!licenseKey) {
            return { success: false, message: 'Please enter a license key' };
        }

        console.log('🔑 Activating license...');
        
        try {
            const deviceId = await this.getDeviceId();
            const deviceName = navigator.userAgent;

            const response = await fetch(`${this.API_URL}/api/license/activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    license_key: licenseKey,
                    device_id: deviceId,
                    device_name: deviceName
                })
            });

            const result = await response.json();

            if (result.valid) {
                // Store license info
                this.licenseData = {
                    license_key: licenseKey,
                    device_id: deviceId,
                    days_remaining: result.days_remaining,
                    expiry_date: result.expiry_date,
                    status: 'active',
                    tier: 'pro',
                    features: ['All Features', 'Unlimited Invoices', 'Digital Signatures', 'Customer Management', 'Reports']
                };
                
                this.isValid = true;
                this.features = this.licenseData.features;
                this.tier = 'pro';
                
                // Save to cache
                await this.saveToCache(this.licenseData);
                await this.updateLastCheck();
                
                console.log('✅ License activated successfully');
                
                return { success: true, message: result.message };
            } else {
                return { success: false, message: result.message };
            }
        } catch (error) {
            console.error('🔴 License activation error:', error);
            return { success: false, message: 'Failed to activate license. Please check your internet connection.' };
        }
    }

    /**
     * Check if a feature is enabled
     */
    hasFeature(featureName) {
        if (!this.isValid) return false;
        return this.features.some(f => 
            f.toLowerCase().includes(featureName.toLowerCase())
        );
    }

    /**
     * Check license tier
     */
    hasTier(tierName) {
        return this.tier.toLowerCase() === tierName.toLowerCase();
    }

    /**
     * Check if license is pro
     */
    isPro() {
        return this.hasTier('pro');
    }

    /**
     * Check if in trial mode
     */
    isTrial() {
        return this.tier === 'trial';
    }

    /**
     * Get days remaining
     */
    getDaysRemaining() {
        if (!this.licenseData) return 0;
        return this.licenseData.days_remaining || 0;
    }

    /**
     * Get trial days remaining
     */
    getTrialDaysRemaining() {
        const trial = this.checkTrialStatus();
        return trial.trialDaysRemaining;
    }

    /**
     * Get license status
     */
    getStatus() {
        if (!this.licenseData) {
            return this.trialStatus?.isExpired ? 'expired' : 'trial';
        }
        return this.licenseData.status || 'inactive';
    }

    /**
     * Check if license is about to expire (less than 30 days)
     */
    isExpiringSoon() {
        return this.getDaysRemaining() < 30 && this.getDaysRemaining() > 0;
    }

    /**
     * Check if trial is expiring soon (less than 7 days)
     */
    isTrialExpiringSoon() {
        const days = this.getTrialDaysRemaining();
        return days <= 7 && days > 0;
    }

    /**
     * Get all license info
     */
    getLicenseInfo() {
        const trial = this.checkTrialStatus();
        
        return {
            isValid: this.isValid,
            tier: this.tier,
            features: this.features,
            daysRemaining: this.getDaysRemaining(),
            trialDaysRemaining: trial.trialDaysRemaining,
            isTrialMode: !this.isValid && !trial.isExpired,
            needsActivation: !this.isValid && trial.isExpired,
            isExpired: trial.isExpired,
            status: this.getStatus(),
            licenseKey: this.licenseData?.license_key,
            expiryDate: this.licenseData?.expiry_date
        };
    }

    /**
     * Deactivate license (logout)
     */
    deactivate() {
        this.clearCache();
        this.isValid = false;
        this.licenseData = null;
        this.features = [];
        this.tier = 'trial';
        
        console.log('🔓 License deactivated');
    }

    /**
     * Save license to encrypted IndexedDB.
     */
    async saveToCache(data) {
        const cacheData = {
            ...data,
            cached_at: Date.now(),
        };
        await secureSet(this.STORAGE_KEY, cacheData);
    }

    /**
     * Load license from encrypted IndexedDB. Returns null if absent or
     * if the entry can't be decrypted (fresh key after migration).
     */
    async loadFromCache() {
        return secureGet(this.STORAGE_KEY);
    }

    /**
     * Clear cached license + last-check timestamp.
     */
    async clearCache() {
        await secureRemove(this.STORAGE_KEY);
        await secureRemove(this.LAST_CHECK_KEY);
    }

    /**
     * Check if we should validate with server (encrypted IDB read).
     */
    async shouldCheckServer() {
        const lastCheck = await secureGet(this.LAST_CHECK_KEY);
        if (!lastCheck) return true;
        return (Date.now() - parseInt(lastCheck)) > this.CHECK_INTERVAL;
    }

    /**
     * Update last check timestamp in encrypted IDB.
     */
    async updateLastCheck() {
        await secureSet(this.LAST_CHECK_KEY, Date.now().toString());
    }
}

export default LicenseManager;
